/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Inject, Input,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import {
  Subject, of,
} from 'rxjs';
import {
  takeUntil, debounceTime, distinctUntilChanged, catchError,
} from 'rxjs/operators';
import { SearchService } from './search.service';
import { SEARCH_CONSTANTS, SEARCH_SCOPES as SEARCH_SCOPE_VALUES, SearchScope } from './models/search.models';
import { ErrorService } from '../../common/error.service';

/**
 * Search form component that provides search input with autocomplete suggestions.
 */
@Component({
  selector: 'bulb-search-form-app',
  templateUrl: './search-form.component.html',
  styleUrls: ['./search-form.component.scss'],
  standalone: false,
})
export class SearchFormComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;

  @Input() userUnitId: string;

  /**
   * True while an API request is in progress.
   * Used to prevent duplicate calls.
   */
  loading: boolean = false;

  /**
   * The current search query
   */
  query = '';

  /**
   * User's "typed so far" query input value, from which suggestions are generated
   */
  prefix = '';

  /**
   * Currently displayed suggestions
   */
  suggestions: any[] = [];

  /**
   * The currently active suggestion, if any
   */
  activeSuggestion: any = '';

  /**
   * Becomes false when the API returns fewer items than expected.
   * Used to stop further loading.
   */
  hasMoreElements: boolean = true;

  /**
   * Whether to include only current portfolio in search
   */
  includeOnlyCurrentPortfolio = false;

  /**
   * Whether to include only current organization(s) in search
   */
  includeOnlyCurrentOrganizations = false;

  /**
   * Whether the user is authenticated
   */
  isAuthenticated = false;

  /**
   * Subject for managing component destruction
   */
  private destroy$ = new Subject<void>();

  /**
   * Subject for search input changes
   */
  private searchSubject$ = new Subject<string>();

  /**
   * Minimum prefix length for suggestions
   */
  readonly MIN_PREFIX_LENGTH = SEARCH_CONSTANTS.MIN_PREFIX_LENGTH;

  readonly SEARCH_SCOPES = SEARCH_SCOPE_VALUES;

  /**
   * Index of the first element to request in the next API call.
   * Used for offset-based pagination, not page number.
   */
  from: number = 0;

  loadingSuggestions = false;

  /**
   * Constructor
   * @param searchService Search service
   * @param router Angular router
   * @param errorService Error service
   * @param bulbAuthService AngularJS auth service
   */
  constructor(
    private searchService: SearchService,
    private router: Router,
    private errorService: ErrorService,
    @Inject('BulbAuthService') private bulbAuthService: any,
  ) {}

  /**
   * Initialize component
   */
  ngOnInit(): void {
    this.isAuthenticated = this.bulbAuthService?.currentUser?.isAuthenticated() || false;

    // Set up debounced search suggestions
    this.searchSubject$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(1000),
        distinctUntilChanged(),
      )
      .subscribe((query) => {
        this.handleSuggestions(query);
      });

    // Listen to route changes to update query
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.handleRouteChange();
        }
      });

    this.handleRouteChange();
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle route changes to update search state
   */
  private handleRouteChange(): void {
    // Check if we're on the search results page
    if (this.router.url.includes('/b/search')) {
      // Extract query from URL if needed
      this.suggestions = [];
      this.activeSuggestion = '';
    }
  }

  /**
   * Clear the search results and suggestions
   */
  clearSearchResults(): void {
    this.query = '';
    this.prefix = '';
    this.suggestions = [];
    this.hasMoreElements = true;
    this.from = 0;
    this.activeSuggestion = '';
  }

  /**
   * Handle search input changes and update suggestions
   * @param query The search query as typed so far
   */
  onSearchChange(query: string): void {
    this.suggestions = [];
    this.hasMoreElements = true;
    this.from = 0;
    this.searchSubject$.next(query);
  }

  /**
   * Handle suggestions based on prefix
   * @param query The search query
   */
  private handleSuggestions(query: string): void {
    this.prefix = query ? query.trim() : '';
    if (this.prefix.length < this.MIN_PREFIX_LENGTH) {
      return;
    }

    if (this.prefix && this.prefix.length >= this.MIN_PREFIX_LENGTH && !this.loading) {
      this.loadSuggestions();
    } else {
      this.suggestions = [];
      this.activeSuggestion = '';
    }
  }

  /**
   * Fetches the next batch of suggestions for infinite scroll.
   * Runs only when `hasMoreElements` is true and sets `loading` to block duplicates.
   * Stops further calls if fewer than 10 items are returned.
   * Keeps max 50 items by trimming the oldest ones.
   */
  loadSuggestions() {
    const shouldRefocusSearchInput = this.from === 0;
    const searchScope = this.getSearchScope();
    const userUnitId = searchScope === SEARCH_SCOPE_VALUES.PORTFOLIO ? this.getPortfolioUserId() : '';
    this.loading = true;
    this.loadingSuggestions = true;

    if (this.hasMoreElements) {
      this.searchService
        .suggest(this.prefix, userUnitId, this.from, searchScope)
        .pipe(
          takeUntil(this.destroy$),
          catchError((error) => {
            this.errorService.handleException(`Error --- Cannot get suggestions => ${error}`);
            this.loading = false;
            this.loadingSuggestions = false;
            return of([]);
          }),
        )
        .subscribe((suggestions) => {
          if (suggestions.length < 10) {
            this.hasMoreElements = false;
          }
          if (this.suggestions.length >= 50) {
            this.suggestions.splice(0, 10);
          }
          if (this.prefix && this.prefix.length >= this.MIN_PREFIX_LENGTH) {
            this.suggestions = this.suggestions.concat(suggestions);
            this.from += 10;
          }
          this.activeSuggestion = '';
          this.loading = false;
          this.loadingSuggestions = false;
          if (shouldRefocusSearchInput) {
            setTimeout(() => this.searchInput?.nativeElement.focus());
          }
        });
    }
  }

  /**
   * Detects when the scroll is near the bottom and triggers loading more suggestions.
   */
  onSuggestionScroll(event) {
    const { target } = event;

    const threshold = 100; // px from bottom
    const position = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (position < threshold && this.hasMoreElements && !this.loading) {
      this.loadSuggestions();
    }
  }

  /**
   * Get the prefix part of a suggestion that matches the user input
   * @param suggestion The suggestion
   * @returns The matching prefix
   */
  getPrefix(suggestion: any): string {
    return suggestion?.name; // substring(0, this.prefix.length);
  }

  /**
   * Get the rest of the suggestion after the prefix
   * @param suggestion The suggestion
   * @returns The non-matching part of the suggestion
   */
  stripPrefix(suggestion: any): string {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = suggestion?.content || '';

    const text = wrapper.textContent?.trim() || '';

    // Take first 50 characters and add "..."
    return text.length > 50 ? `${text.substring(0, 50)}...` : text;
  }

  /**
   * Set the active suggestion
   * @param suggestion The suggestion to activate
   */
  setActiveSuggestion(suggestion: any): void {
    const index = this.suggestions.indexOf(suggestion);
    if (index >= 0) {
      this.activeSuggestion = suggestion;
      this.query = this.activeSuggestion.name;
    }
  }

  private executeSearch() {
    if (!this.query) {
      return;
    }

    this.suggestions = [];
    this.searchService.recordSearchEvent(this.query);

    const searchScope = this.getSearchScope();
    const userUnitId = searchScope === SEARCH_SCOPE_VALUES.PORTFOLIO ? this.getPortfolioUserId() : '';

    window.searchResults(this.query, userUnitId, searchScope);
  }

  /**
   * Handle keyboard navigation in suggestions
   * @param event The keyboard event
   */
  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Tab':
        event.preventDefault();
        this.incrementActiveSuggestion(event.shiftKey ? -1 : 1);
        if (this.activeSuggestion) {
          this.query = this.activeSuggestion.name;
        }
        break;
      case 'Escape':
        event.preventDefault();
        if (this.query === this.prefix) {
          // Clear query and suggestions
          this.clearSearchResults();
        } else {
          // Clear active suggestion
          this.activeSuggestion = '';
          this.query = this.prefix;
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.incrementActiveSuggestion(1);
        if (this.activeSuggestion) {
          this.query = this.activeSuggestion.name;
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.incrementActiveSuggestion(-1);
        if (this.activeSuggestion) {
          this.query = this.activeSuggestion.name;
        }
        break;
    }
  }

  /**
   * Navigate to the next or previous suggestion
   * @param increment 1 for next, -1 for previous
   */
  private incrementActiveSuggestion(increment: number): void {
    let index = this.suggestions.indexOf(this.activeSuggestion);
    index += increment;

    if (index < 0) {
      index = this.suggestions.length - 1;
    } else if (index >= this.suggestions.length) {
      index = 0;
    }

    this.activeSuggestion = this.suggestions[index] || '';
  }

  /**
   * Toggle the "only current portfolio" filter
   */
  toggleOnlyCurrentPortfolio(): void {
    this.setSearchScope(
      this.includeOnlyCurrentPortfolio ? SEARCH_SCOPE_VALUES.ALL : SEARCH_SCOPE_VALUES.PORTFOLIO,
    );
  }

  /**
   * Toggle the "only current organization(s)" filter
   */
  toggleOnlyCurrentOrganizations(): void {
    this.setSearchScope(
      this.includeOnlyCurrentOrganizations ? SEARCH_SCOPE_VALUES.ALL : SEARCH_SCOPE_VALUES.ORGANIZATIONS,
    );
  }

  /**
   * Apply a new search scope and refresh suggestions if needed.
   * @param searchScope The selected search scope.
   */
  setSearchScope(searchScope: SearchScope): void {
    this.includeOnlyCurrentPortfolio = searchScope === SEARCH_SCOPE_VALUES.PORTFOLIO;
    this.includeOnlyCurrentOrganizations = searchScope === SEARCH_SCOPE_VALUES.ORGANIZATIONS;
    this.hasMoreElements = true;
    this.suggestions = [];
    this.from = 0;
    this.handleSuggestions(this.query);
  }

  /**
   * Determine the active search scope.
   * @returns The active search scope.
   */
  private getSearchScope(): SearchScope {
    if (this.includeOnlyCurrentPortfolio) {
      return SEARCH_SCOPE_VALUES.PORTFOLIO;
    }

    if (this.includeOnlyCurrentOrganizations) {
      return SEARCH_SCOPE_VALUES.ORGANIZATIONS;
    }

    return SEARCH_SCOPE_VALUES.ALL;
  }

  /**
   * Submit the search form
   */
  onSubmit(): void {
    this.executeSearch();
  }

  /**
   * Get the current portfolio user ID
   * @returns The portfolio user ID
   */
  private getPortfolioUserId(): string | null {
    return this.userUnitId;
  }

}
